import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';
import {
  BUSINESS_TYPE_META,
  businessTypeMeta,
} from '@/shared/api/types/tenant';
import { buildScopedPath, scopedRoutes, type ScopedRouteKey } from '@/app/routes';
import { useBusinessTypeMutation } from './useBusinessTypeMutation';

/**
 * Идэвхтэй company-ийн `BusinessType`-аас хамаарч main content-ыг шүүнэ:
 *
 *   - `classic` (10..13): хуучин POS/retail UI бүрэн харагдана (`<Outlet />`).
 *   - `new` (20, CarWash): "Тун удахгүй" placeholder — UI хөгжиж байна.
 *   - `pending` (30..60): "Энэ төрөл одоогоор идэвхгүй" placeholder.
 *   - `unknown` (DB-д үлдсэн хуучин 0..7): "Тодорхойгүй" placeholder, switcher-аар
 *      шинэ төрөл сонгох заавартай.
 *
 * Tenant-level admin route-ууд (profile, company-settings, invoices, ...) нь
 * gate-ийг өнгөрнө — хэрэглэгчид тэдгээрээс scope/plan/business-type-ээ засах
 * боломж үлдэнэ. Header (TenantSwitcher + BusinessTypeSwitcher + UserMenu)
 * бүх төлөвт харагдсаар үлдэнэ — ShellLayout өөрөө wrap хийдэг.
 */
const BYPASS_ROUTES: ScopedRouteKey[] = [
  'profile',
  'companySettings',
  'invoices',
  'invoiceDetail',
  'terminals',
  'tickets',
  'roadmap',
];

function isBypassPath(pathname: string): boolean {
  // pathname = `/<tenantId>/<companyId>/<sub>...` — sub-аар таних.
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length < 3) return false;
  const sub = parts[2];
  return BYPASS_ROUTES.some((k) => scopedRoutes[k] === sub);
}

export function BusinessTypeGate() {
  const scope = useTenantScope();
  const loc = useLocation();

  // Scope бүрэн resolve болоогүй (companies query pending) бол outlet-ыг
  // үлдээгээд хүлээнэ — ShellInner-ийн loading placeholder Suspense-ээр зохицуулна.
  if (scope.status !== 'ok' || scope.companyBusinessType == null) {
    return <Outlet />;
  }

  const meta = businessTypeMeta(scope.companyBusinessType);

  if (meta.category === 'classic') return <Outlet />;
  if (isBypassPath(loc.pathname)) return <Outlet />;

  return <GatePlaceholder meta={meta} />;
}

function GatePlaceholder({
  meta,
}: {
  meta: ReturnType<typeof businessTypeMeta>;
}) {
  const scope = useTenantScope();
  const mut = useBusinessTypeMutation();

  const headline =
    meta.category === 'new'
      ? `${meta.label}-ын шинэ интерфейс бэлдэгдэж байна`
      : meta.category === 'pending'
        ? `«${meta.label}» бизнесийн төрлийн интерфейс одоогоор идэвхгүй`
        : 'Бизнесийн төрөл тодорхойгүй байна';

  const body =
    meta.category === 'new'
      ? 'Угаалгын газрын бүтэн POS урсгал тусгайлан хөгжиж байна. Доорхоос одоо ажиллах боломжтой төрөл сонгож үндсэн UI-руу шилжээрэй.'
      : meta.category === 'pending'
        ? 'Энэ бизнесийн төрөлд зориулсан UI хараахан бэлэн болоогүй байна. Доорхоос идэвхтэй төрөл сонгож үндсэн UI-руу шилжээрэй.'
        : 'Танай байгууллагын бизнесийн төрөл шинэ системд хараахан тогтоогдоогүй байна. Доорхоос төрөл сонгож үндсэн UI-руу шилжээрэй.';

  const classicMetas = BUSINESS_TYPE_META.filter((b) => b.category === 'classic');

  return (
    <div className="fade-in" style={{ maxWidth: 720, margin: '60px auto', padding: '0 16px' }}>
      <div
        className="ts-card"
        style={{
          padding: '36px 32px',
          textAlign: 'center',
          borderRadius: 18,
        }}
      >
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 24,
            background: 'rgba(0, 194, 168, 0.10)',
            color: 'var(--ts-brand)',
            margin: '0 auto 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 44,
          }}
        >
          <i className={`bi ${meta.icon}`} />
        </div>
        <h1 className="ts-page-title" style={{ marginBottom: 10 }}>
          {headline}
        </h1>
        <p style={{ color: 'var(--ts-muted)', lineHeight: 1.6, marginBottom: 24 }}>{body}</p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 360,
            margin: '0 auto 20px',
          }}
        >
          <div
            style={{
              fontSize: '0.7rem',
              color: 'var(--ts-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 4,
              textAlign: 'left',
            }}
          >
            Бизнесийн төрөл сонгох
          </div>
          {classicMetas.map((b) => (
            <button
              key={b.value}
              type="button"
              className="ts-switcher-item"
              onClick={() => mut.mutate(b.value)}
              disabled={mut.isPending}
              style={{ width: '100%' }}
            >
              <i className={`bi ${b.icon} ts-switcher-item-icon`} />
              <span className="ts-switcher-item-label">{b.label}</span>
            </button>
          ))}
          {mut.isPending ? (
            <div className="ts-switcher-loading" style={{ marginTop: 4 }}>
              <span className="spinner-border spinner-border-sm mr-2" /> Шинэчилж байна…
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            to={buildScopedPath('companySettings', scope)}
            className="ts-btn-outline"
            style={{ textDecoration: 'none' }}
          >
            <i className="bi bi-gear" /> Байгууллагын тохиргоо
          </Link>
          <Link
            to={buildScopedPath('profile', scope)}
            className="ts-btn-outline"
            style={{ textDecoration: 'none' }}
          >
            <i className="bi bi-person" /> Профайл
          </Link>
        </div>
      </div>
    </div>
  );
}
